import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CookieContext = createContext();

export const COOKIE_CATEGORIES = {
  necessary: 'necessary',
  analytics: 'analytics'
};

const COOKIE_CONSENT_KEY = 'wolkenhelden_cookie_consent';
const COOKIE_CONSENT_VERSION = '1.0';

export function CookieProvider({ children }) {
  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.version === COOKIE_CONSENT_VERSION) {
          setConsent(parsed.consent);
          setShowBanner(false);
        } else {
          // Version changed, show banner again
          setShowBanner(true);
        }
      } catch (e) {
        setShowBanner(true);
      }
    } else {
      setShowBanner(true);
    }
    setIsLoaded(true);
  }, []);

  // Save consent to localStorage
  const saveConsent = useCallback((newConsent) => {
    const data = {
      version: COOKIE_CONSENT_VERSION,
      consent: newConsent,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(data));
    setConsent(newConsent);
    setShowBanner(false);
  }, []);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    saveConsent({
      [COOKIE_CATEGORIES.necessary]: true,
      [COOKIE_CATEGORIES.analytics]: true
    });
  }, [saveConsent]);

  // Accept only necessary cookies
  const acceptNecessary = useCallback(() => {
    saveConsent({
      [COOKIE_CATEGORIES.necessary]: true,
      [COOKIE_CATEGORIES.analytics]: false
    });
  }, [saveConsent]);

  // Update specific category
  const updateConsent = useCallback((category, value) => {
    const newConsent = {
      ...consent,
      [category]: value
    };
    saveConsent(newConsent);
  }, [consent, saveConsent]);

  // Check if category is allowed
  const hasConsent = useCallback((category) => {
    if (!consent) return false;
    if (category === COOKIE_CATEGORIES.necessary) return true;
    return consent[category] === true;
  }, [consent]);

  // Open settings (show banner again)
  const openSettings = useCallback(() => {
    setShowBanner(true);
  }, []);

  const value = {
    consent,
    showBanner,
    isLoaded,
    acceptAll,
    acceptNecessary,
    updateConsent,
    hasConsent,
    openSettings,
    saveConsent
  };

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieProvider');
  }
  return context;
}
